/**
 * AAMVA parser for the PDF417 barcode on the back of US (and Canadian) driving
 * licences. The barcode's bytes are DECODED to this text payload elsewhere (a
 * barcode reader in the browser, Phase 2); this parser is pure and takes the
 * decoded string. Because a barcode is read exactly, its fields are the most
 * trustworthy of the three sources — confidence is high and the source is
 * 'barcode'.
 *
 * The payload is newline-separated elements, each a three-letter code and its
 * value (AAMVA D20 §D.12): DCS surname, DAC first name, DAD middle, DBB birth,
 * DBA expiry, DBD issue, DAQ licence number, DCA vehicle class.
 */
import type { ScanField, ScannedIdentity } from '../ocr-interface'

/** Each element sits on its own line: a 3-letter code then its value. */
const ELEMENT_RE = /^([A-Z]{3})(.*)$/

function elements(payload: string): Map<string, string> {
  const out = new Map<string, string>()
  for (const raw of payload.split(/[\r\n]+/)) {
    const m = ELEMENT_RE.exec(raw.trim())
    if (!m) continue
    const code = m[1] ?? ''
    const value = (m[2] ?? '').trim()
    if (value && !out.has(code)) out.set(code, value)
  }
  return out
}

/** AAMVA dates are 8 digits: MMDDYYYY for US, CCYYMMDD for Canada. */
export function parseAamvaDate(raw: string): string | null {
  const s = raw.replace(/\D/g, '')
  if (s.length !== 8) return null
  const tries: Array<[number, number, number]> = [
    [Number(s.slice(4, 8)), Number(s.slice(0, 2)), Number(s.slice(2, 4))], // MMDDYYYY (US)
    [Number(s.slice(0, 4)), Number(s.slice(4, 6)), Number(s.slice(6, 8))], // CCYYMMDD (CA)
  ]
  for (const [y, m, d] of tries) {
    if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1900 || y > 2100) continue
    const iso = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    const date = new Date(`${iso}T00:00:00Z`)
    if (!Number.isNaN(date.getTime()) && date.getUTCDate() === d) return iso
  }
  return null
}

function field<T>(value: T): ScanField<T> {
  // A barcode is read exactly, not OCR'd: high, fixed confidence.
  return { value, confidence: 0.98, source: 'barcode' }
}

const titleCase = (s: string) => s.replace(/\s+/g, ' ').trim().toUpperCase()

/**
 * Parse a decoded AAMVA PDF417 payload. Returns null when it is not AAMVA (no
 * recognisable elements), so the caller can fall back to the front OCR or to
 * manual entry. A US licence carries no CaRGOS document type (an ID card or
 * passport does); it contributes the licence number, dates, name and birth.
 */
export function parseAamva(payload: string): ScannedIdentity | null {
  const e = elements(payload)
  if (!e.has('DAQ') && !e.has('DCS')) return null

  const identity: Record<string, unknown> = {}

  const surname = e.get('DCS')
  if (surname) identity.surname = field(titleCase(surname))

  // DAC first name (+ DAD middle); older payloads put all given names in DCT.
  const first = e.get('DAC') ?? e.get('DCT')
  const middle = e.get('DAD')
  const given = [first, middle].filter(Boolean).join(' ').trim()
  if (given) identity.givenNames = field(titleCase(given))

  const birth = e.get('DBB')
  if (birth) {
    const iso = parseAamvaDate(birth)
    if (iso) identity.birthDate = field(iso)
  }
  const expiry = e.get('DBA')
  if (expiry) {
    const iso = parseAamvaDate(expiry)
    if (iso) identity.licenceExpiry = field(iso)
  }
  const issued = e.get('DBD')
  if (issued) {
    const iso = parseAamvaDate(issued)
    if (iso) identity.licenceIssued = field(iso)
  }

  const number = e.get('DAQ')
  if (number) identity.licenceNumber = field(number.replace(/\s+/g, '').toUpperCase())

  // DCG is the ISSUING country (USA / CAN), not the holder's citizenship: a US
  // licence says nothing about nationality, so it fills no field (OPS-1).

  const classes = e.get('DCA')
  if (classes) {
    const cats = classes
      .toUpperCase()
      .split(/[\s,]+/)
      .map((c) => c.trim())
      .filter(Boolean)
    if (cats.length > 0) identity.licenceCategories = field(cats as readonly string[])
  }

  return identity as ScannedIdentity
}
